<template>
	<div class="ui segment manager-tab" :style="{ display: displayStatus }" :class="dynamicClass">
		<component :is="component" v-if="component" v-bind="componentProps" v-on="componentEvents" />
		<div class="ui warning message tab-warning-message" v-if="tab.warningMessage">{{ tab.warningMessage }}</div>
		
		<div class="ui inverted dimmer tab-loader" :class="{ active: loading && !loadingError }">
			<div class="ui text loader">Loading</div>
		</div>
		<div class="ui error message" v-if="loadingError">
			Failed to load tab - please refresh to try again
		</div>
	</div>
</template>

<script>
import { sleep } from '../utils/sleep';
const RETRY_DELAY = 500;

export default {
	props: {
		active: {
			default: false
		},
		tab: {
			default: function() {
				return {};
			}
		}
	},
	data: function() {
		return {
			version: this.tab.version,
			loading: true,
			loaded: false,
			loadingError: false,
			component: null,
			componentProps: {},
			componentEvents: {}
		};
	},
	computed: {
		displayStatus: function() {
			if(this.active) {
				return '';
			} else {
				return 'none';
			}
		},
		dynamicClass: function() {
			var classes = {};
			if(this.tab.warningMessage) {
				classes['hide-content'] = true;
			}

			if(this.tab.html.includes('.vue')) {
				['v-application', 'v-application--is-ltr', 'v-application-tab', 'tab-component'].forEach(name => classes[name] = true);
				if(this.tabComponent?.hasCardOutline) {
					classes['tab-has-card-outline'] = true;
				}
			}

			return classes;
		},
		tabComponent() {
			return window.tabComponents[this.tab.html.replace('.vue', '')];
		}
	},
	methods: {
		load: function() {
			this.component = null;
			this.loadingError = false;
			let loadingTab = this.tab;
			if(this.tab.html.includes('.html')) {
				$.ajax({
					url: this.tab.html,
					dataType: 'html',
					encodeParamaters: false,
					headers: {
						'X-Remote': true
					},
					success: (data) => {
						$(this.$el).append(data);
						$(this.$el).find('[data-i18n]').each(function() {
							var name = $(this).attr('data-i18n');
							var label = i18n.t(name);
							if(name !== label) {
								this.innerText = label;
							}
						});
						this.doneLoadingTab(loadingTab);
					}
				});
			} else if(this.tab.html.includes('.vue')) {
				let component = this.tabComponent;

				let componentProps = {};
				if(component?.props) {
					componentProps = {...componentProps, ...component.props() };
				}
				this.componentProps = componentProps;

				let componentEvents = {
					back: () => {
						window.returnToPreviousTab();
					},
					done: () => {
						window.returnToPreviousTab();
					}
				};
				if(component?.events) {
					componentEvents = { ...componentEvents, ...component.events };
				}
				this.componentEvents = componentEvents;

				if(component?.component) {
					component = component.component;
				}

				if(typeof component === 'function') {
					component().then(importResult => {
						this.finishLoadingAsyncComponent(importResult, loadingTab);
					}).catch(async e => {
						console.error(`Failed to load tab.  Retrying in ${RETRY_DELAY}:`, e);
						await sleep(RETRY_DELAY);
						component().then(async importResult => {
							this.finishLoadingAsyncComponent(importResult, loadingTab);
						}).catch(e => {
							console.error('Failed to load tab:', e);
							this.loadingError = true;
						});
					});
				} else {
					this.component = component;
					this.doneLoadingTab(loadingTab);
				}
			}
		},
		finishLoadingAsyncComponent(importResult, loadingTab) {
			// NOTE: We are currently not stopping multiple loads so we need to stop multiple suceeding later from overwriting each other
			if(!this.loading) {
				return;
			}

			let actualComponent = importResult.default;
			this.component = actualComponent;
			this.doneLoadingTab(loadingTab);
		},
		doneLoadingTab(tab) {
			this.loading = false;
			tab.loaded = true;

			this.onFirstLoad();
			this.onLoad();
		},
		onFirstLoad: function() {
			this.loaded = true;

			// TODO: We can probably just pass active project directly to this component instead of relying on these fragile global callbacks
			if(this.tabComponent?.registerForSchoolChange && window.registerForSchoolChange) {
				window.registerForSchoolChange({
					tab: this.tab.html,
					componentProps: this.componentProps,
					onSchoolChangeHandler: (school) => {
						this.tabComponent.registerForSchoolChange.onSchoolChangeHandler(this.componentProps, school);
					}
				});
			}
			if(this.tabComponent?.registerForSchoolListChange && $.schoolListManager) {
				$.schoolListManager.register({
					tab: this.tab.html,
					onSchoolChangeHandler: (schools) => {
						this.tabComponent.registerForSchoolListChange(this.componentProps, schools);
					}
				});
			}
			if(this.tabComponent?.registerSubTabForSchoolChange && window.registerSubTabForSchoolChange) {
				window.registerSubTabForSchoolChange(this.tab.html);
			}

			if($.runManagementTabTutorial) {
				try {
					hopscotch.endTour();
				} catch(e) {
					console.error('Failed to end hopscotch tour: ', e);
				}
				$.runManagementTabTutorial(this.tab.html);
			}
		},
		onLoad: function() {
			if($.schoolListManager) {
				$.schoolListManager.runDelayedListener(this.tab.html);
				$.schoolChangeManager.runDelayedListener(this.tab.html);
			}

			var displayPage = this.tab.html.replace('.html', '').replace('.php', '').replace('.vue', '');
			if(window.Intercom) {
				window.Intercom('trackEvent', 'navigated-to-' + displayPage + '-tab');
				window.Intercom('update');
			}
		},

		runOnActive: function() {
			if(this.loaded && this.tab.forceRefresh || this.tab.version > this.version) {
				if(this.component) {
					const savedComponent = this.component;
					this.component = null;
					this.loading = true;
					this.$nextTick().then(() => {
						let componentProps = {};
						if(this.tabComponent?.props) {
							componentProps = {...componentProps, ...this.tabComponent.props() };
						}
						this.componentProps = componentProps;

						this.component = savedComponent;
						this.loading = false;
					});
				} else {
					$(this.$el).children().not('.ui.dimmer.tab-loader').remove();
					this.loaded = false;
					this.loading = true;
					this.version = this.tab.version;
				}
			}

			if(this.loaded) {
				this.onLoad();
			} else {
				this.load();
			}
		}
	},
	watch: {
		active: function() {
			if(this.active) {
				this.runOnActive();
			}
		}
	},
	mounted() {
		// Some tabs get added while already active
		if(this.active) {
			this.runOnActive();
		}
	}
};
</script>

<style>
/*
	Datatables is retarded and won't shrink the size based on the screen resolution, so we need this to stop the edges from just getting clipped and ignored
	Adding overflow scrollbars breaks tooltips for buttons near the top of the screen, so only apply when we need to
*/
@media (max-width: 1499px) and (min-width: 770px) {
	.manager-tab {
		overflow-x: auto;
	}
}

.manager-tab > h2.ui.header:first-of-type {
	display: none;
}

.manager-tab.hide-content > :not(script):not(.tab-warning-message) {
	display: none;
}

.manager-app .content > .manager-tab.v-application.v-application-tab {
	display: block;
	text-align: center;
}
.manager-app .content > .manager-tab.v-application.v-application-tab:has(> .v-sheet), .manager-app .content > .manager-tab.v-application.v-application-tab.tab-has-card-outline {
	margin: 0;
	padding: 0;
	border: 0;
	border-radius: initial;
}
.manager-app .content > .manager-tab.v-application.v-application-tab.tab-component {
	text-align: left;
}

.manager-app .content > .manager-tab td[data-tooltip]:after, .manager-app .content > .manager-tab td > button[data-tooltip]:after {
	white-space: pre;
}
</style>